/**
 * Persist CollapsiblePane expanded/collapsed state to localStorage.
 *
 * Storage key shape: policylens:collapsible:{pageId}
 * Value shape:        { [paneId]: boolean }
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'policylens:collapsible';

function keyFor(pageId) {
    return `${STORAGE_PREFIX}:${pageId}`;
}

function readAll(pageId) {
    try {
        const raw = window.localStorage.getItem(keyFor(pageId));
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function writeAll(pageId, value) {
    try {
        window.localStorage.setItem(keyFor(pageId), JSON.stringify(value));
    } catch {
        // localStorage quota or SSR — ignore.
    }
}

/**
 * @param {string} pageId
 * @param {string} paneId
 * @param {boolean} defaultExpanded
 * @returns {[boolean, (next: boolean) => void]}
 */
export function useCollapsibleState(pageId, paneId, defaultExpanded) {
    const [expanded, setExpandedState] = useState(() => {
        const all = readAll(pageId);
        return typeof all[paneId] === 'boolean' ? all[paneId] : defaultExpanded;
    });

    // If the pageId or paneId changes at runtime, re-read.
    useEffect(() => {
        const all = readAll(pageId);
        if (typeof all[paneId] === 'boolean' && all[paneId] !== expanded) {
            setExpandedState(all[paneId]);
        }
        // NOTE: react-hooks/exhaustive-deps is not currently loaded by this build's ESLint
        // config (see .claude/lessons/eslint-plugin-react-hooks-breaks-cra-build.md) — an
        // actual `eslint-disable-next-line` directive here fails the build because ESLint
        // can't find the rule to disable. `expanded` is intentionally excluded from this
        // effect's deps to avoid re-running on every local toggle, only on pageId/paneId
        // change. If the full `react-app` ESLint config is ever wired in, re-add a real
        // disable directive at that point.
    }, [pageId, paneId]);

    // CollapsiblePaneGroup's Expand all / Collapse all writes localStorage for every pane on the
    // page and then broadcasts this same-window custom event (the native `storage` event never
    // fires in the tab that made the change, only in *other* tabs — see MDN — so it cannot be
    // used to update already-mounted panes here). Without this listener the group's buttons wrote
    // state that no mounted <CollapsiblePane> ever picked up.
    useEffect(() => {
        const handler = (e) => {
            if (typeof e?.detail?.expanded === 'boolean') setExpandedState(e.detail.expanded);
        };
        window.addEventListener(`policylens:collapsible:${pageId}`, handler);
        return () => window.removeEventListener(`policylens:collapsible:${pageId}`, handler);
    }, [pageId]);

    const setExpanded = useCallback(
        (next) => {
            setExpandedState(next);
            const all = readAll(pageId);
            all[paneId] = next;
            writeAll(pageId, all);
        },
        [pageId, paneId],
    );

    return [expanded, setExpanded];
}

/**
 * For CollapsiblePaneGroup — set every pane's expanded state in one shot.
 */
export function setAllPanesForPage(pageId, expanded, paneIds) {
    const all = readAll(pageId);
    for (const id of paneIds) all[id] = expanded;
    writeAll(pageId, all);
    // Signal a storage event to any listeners in this window.
    try {
        window.dispatchEvent(new StorageEvent('storage', { key: keyFor(pageId) }));
    } catch {
        // no-op
    }
}
